import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast as sonnerToast } from "sonner";
import { userDetailSelect } from "Layout/slice/selector";
import { togglePurchasePlanModal } from "Layout/slice/index";
import {
  SettingsPlanBadgeIcon,
  SettingsCalendarIcon,
  SettingsCardIcon,
} from "assets/icons";
import { useBillingHistory } from "features/Account/hook/useBillingHistory";
import { updatePaymentMethod } from "features/Account/v2/PurchasePlan/service";
import { unsubscribePlan, continuePremiumPlan } from "features/Plans/service";
import { DowngradeConfirmModal } from "features/Account/v2/ChoosePlanModal/DowngradeConfirmModal";
import { ReactivateModal } from "features/Account/v2/ChoosePlanModal/ReactivateModal";
import { openNewTab, parseErrorMessage } from "utils";
import { getBillingState } from "./billingState";
import { BillingHistoryTable } from "./BillingHistoryTable";
import {
  PanelContainer,
  PlanHeaderCard,
  RenewalRow,
  PaymentRow,
  DarkButton,
  OutlineButton,
} from "./BillingPanel.styles";

const reloadSoon = () => setTimeout(() => window.location.reload(), 2000);

export const BillingPanel = () => {
  const dispatch = useDispatch();
  const user = useSelector(userDetailSelect);
  const state = getBillingState(user);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [isUpdatingCard, setIsUpdatingCard] = useState(false);
  const isCancelling = useRef(false);
  const canReactivate = state.cancelScheduled || state.scheduledChange;

  const {
    isLoading: isHistoryLoading,
    isFetchingNextPage,
    billingHistory,
    hasNextPage,
    fetchNextPage,
  } = useBillingHistory();

  const handleAdjustPlan = () => dispatch(togglePurchasePlanModal(true));

  const handleCancelPlan = async () => {
    // The confirm dialog re-enables its button when this resolves and the page
    // only reloads 2s later, so guard against a second request.
    if (isCancelling.current) return;
    isCancelling.current = true;
    try {
      await unsubscribePlan(null);
      setShowCancelModal(false);
      sonnerToast.success("Downgrade to Surmount Free scheduled");
      reloadSoon();
    } catch (error) {
      isCancelling.current = false;
      sonnerToast.error(parseErrorMessage(error));
    }
  };

  const handleReactivate = async () => {
    try {
      await continuePremiumPlan();
      sonnerToast.success("Subscription reactivated");
      reloadSoon();
    } catch (error) {
      sonnerToast.error(parseErrorMessage(error));
    } finally {
      setShowReactivateModal(false);
    }
  };

  const handleUpdateCard = async () => {
    setIsUpdatingCard(true);
    try {
      const { url } = await updatePaymentMethod();
      openNewTab(url, "_self");
    } catch (error) {
      sonnerToast.error(parseErrorMessage(error));
      setIsUpdatingCard(false);
    }
  };

  const handleViewInvoice = (url) => {
    if (!url || url === "No invoice") {
      sonnerToast.error("No invoice found");
      return;
    }
    openNewTab(url);
  };

  return (
    <PanelContainer>
      <PlanHeaderCard $tier={state.tier}>
        <div className="plan-identity">
          <div className="plan-badge">
            <SettingsPlanBadgeIcon />
          </div>
          <div className="plan-text">
            <div className="plan-title-row">
              <span className="plan-title">{state.planTitle}</span>
              {state.interval && (
                <span className="plan-interval">{state.interval}</span>
              )}
            </div>
            {state.description && (
              <span className="plan-description">{state.description}</span>
            )}
          </div>
        </div>
        {canReactivate ? (
          <OutlineButton type="button" onClick={handleAdjustPlan}>
            Adjust plan
          </OutlineButton>
        ) : (
          <DarkButton type="button" onClick={handleAdjustPlan}>
            Adjust plan
          </DarkButton>
        )}
      </PlanHeaderCard>

      {state.modifying && (
        <p className="panel-notice">New plan details will be updated soon!</p>
      )}

      {state.showRenewal && (
        <RenewalRow>
          <div className="renewal-text">
            <SettingsCalendarIcon />
            {state.renewalMessage}
          </div>
          {canReactivate ? (
            <DarkButton
              type="button"
              onClick={() => setShowReactivateModal(true)}
            >
              Reactivate current plan
            </DarkButton>
          ) : (
            <OutlineButton
              type="button"
              onClick={() => setShowCancelModal(true)}
            >
              Cancel plan
            </OutlineButton>
          )}
        </RenewalRow>
      )}

      <div className="divider" />

      <section>
        <h3 className="panel-heading">Payment method</h3>
        <PaymentRow>
          <div className="card-info">
            <div className="card-icon">
              <SettingsCardIcon />
            </div>
            <div className="card-text">
              {state.card ? (
                <>
                  <span className="card-title">
                    {`${state.card.brand} ending in ${state.card.last4}`}
                  </span>
                  <span className="card-sub">{`Expires ${state.card.expiry}`}</span>
                </>
              ) : (
                <span className="card-title">No payment method on file</span>
              )}
              {state.paymentFailed && (
                <span className="card-warning">
                  Your last payment failed. Update your payment method.
                </span>
              )}
            </div>
          </div>
          {!state.isFree && (
            <OutlineButton
              type="button"
              disabled={isUpdatingCard}
              onClick={handleUpdateCard}
            >
              Update
            </OutlineButton>
          )}
        </PaymentRow>
      </section>

      <div className="divider" />

      <section>
        <h3 className="panel-heading">Billing history</h3>
        <BillingHistoryTable
          rows={billingHistory}
          isLoading={isHistoryLoading}
          hasNextPage={!!hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={fetchNextPage}
          onViewInvoice={handleViewInvoice}
        />
      </section>

      <DowngradeConfirmModal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        onConfirm={handleCancelPlan}
        plan={null}
        currentPlanName={state.planName}
        periodEnd={state.renewalDateLong}
        isFreeDowngrade
      />
      <ReactivateModal
        show={showReactivateModal}
        onHide={() => setShowReactivateModal(false)}
        onConfirm={handleReactivate}
        currentPlan={user?.surmount_plan}
        renewalDate={state.renewalDateLong}
      />
    </PanelContainer>
  );
};
